import { type FC, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import { ActiveRoleContext, type ActiveRoleContextType } from '@/contexts/ActiveRoleContext';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { setActiveRoleHeader } from '@/utils/activeRoleHeader';
import { persistActiveRole } from '@/utils/persistActiveRole';

const GET_FEATURES_FOR_ROLE = gql`
  query GetFeaturesForRole($role: String!) {
    featuresForRole(role: $role)
  }
`;

interface FeaturesForRoleResponse {
  featuresForRole: string[];
}

// Storage key is scoped per-user AND per-tenant.
//
// Per-user: a shared browser (kiosk, sequential logins) must not leak one
// user's role choice onto the next — even when they share role names.
//
// Per-tenant: each tenant realm has its OWN role catalog. When you tenant-
// switch into a child, your parent role (e.g. "soc_operator") usually isn't a
// role in the child's realm, so the mismatch-reset below picks a child role.
// If the key were user-only, that write would CLOBBER the parent's stored
// choice, and exiting back to the parent would land you on the wrong role
// (the reported bug: enter as SOC Operator → exit → dropped to Tenant Admin).
// Keying on the tenant too means each tenant remembers its own active role
// independently, so returning to the parent restores exactly what you left.
const STORAGE_KEY_PREFIX = 'matrix-active-role';
const storageKeyFor = (
  userId: string | undefined | null,
  tenant: string | undefined | null
) => {
  const u = userId || 'anon';
  const t = (tenant && tenant.trim()) || 'default';
  return `${STORAGE_KEY_PREFIX}:${u}:${t}`;
};

/**
 * Provider for active role state management.
 *
 * Reads available roles from UserInfoContext and manages the active role
 * selection with localStorage persistence.
 */
export const ActiveRoleProvider: FC<PropsWithChildren> = ({ children }) => {
  const { userInfo, isLoading: userLoading } = useContext(UserInfoContext);
  // Needed to drop cached results authored under the PREVIOUS role — see the
  // long note in `setActiveRole`. Safe here: ActiveRoleProvider is mounted
  // inside PrivateApolloProvider (UserInfoChecker), so a client always exists.
  const apolloClient = useApolloClient();
  const [activeRole, setActiveRoleState] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [roleFeatures, setRoleFeatures] = useState<string[]>([]);
  // See ActiveRoleContext#roleFeaturesResolved (#3942).
  const [roleFeaturesResolved, setRoleFeaturesResolved] = useState(false);
  const storageKey = storageKeyFor(userInfo?.id, userInfo?.devoDomain);

  // Lazy query to fetch role features - only called when role changes.
  //
  // We DERIVE `roleFeatures` from the hook's returned `data` (via the effect
  // below) rather than only from an `onCompleted` callback. Apollo 3.14 warns
  // that setting local state from `onCompleted` is unreliable (it can skip
  // firing on cache/dedup paths), which left `roleFeatures` stuck at `[]` — and
  // once `useEffectiveFeatures` stopped treating `[]` as "full ceiling", that
  // empty state hid every feature-gated surface. Deriving from `data` is the
  // Apollo-recommended, reliable source of truth.
  const [
    fetchRoleFeatures,
    { data: roleFeaturesData, error: roleFeaturesError, loading: isLoadingFeatures },
  ] = useLazyQuery<FeaturesForRoleResponse>(GET_FEATURES_FOR_ROLE, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (roleFeaturesData?.featuresForRole) {
      setRoleFeatures(roleFeaturesData.featuresForRole);
      setRoleFeaturesResolved(true);
    }
  }, [roleFeaturesData]);

  // On query failure, resolve to the zero-grants deny path rather than leaving
  // mapped routes permanently fail-open waiting on a fetch that will never land.
  useEffect(() => {
    if (!roleFeaturesError) return;
    console.warn('[ActiveRole] featuresForRole fetch failed; falling back to zero-grants', roleFeaturesError);
    setRoleFeatures([]);
    setRoleFeaturesResolved(true);
  }, [roleFeaturesError]);

  // Roles that are Keycloak plumbing, not operational identities the user would
  // ever "operate as". Hidden from the switcher so the list shows only
  // meaningful roles. Narrowing on the backend retains these implicitly, so
  // hiding them here is purely cosmetic.
  const NOISE_ROLES = useMemo(
    () => new Set(['offline_access', 'uma_authorization']),
    []
  );

  // Get available (switchable) roles from userInfo, minus KC plumbing roles.
  const availableRoles = useMemo(() => {
    return (userInfo?.roles ?? []).filter(
      (r) => !NOISE_ROLES.has(r) && !r.startsWith('default-roles-')
    );
  }, [userInfo?.roles, NOISE_ROLES]);

  // Drop every cached GraphQL result and re-run active queries.
  //
  // The active role is NOT part of Apollo's cache key: it travels as the
  // `X-Matrix-Active-Role` header from a module-level variable (see
  // `utils/activeRoleHeader.ts`), because Apollo links live outside the React
  // tree. That means one query document maps to ONE cache entry no matter which
  // role produced it — so after a switch, any query using a cache-reading
  // fetchPolicy (`cache-and-network`, `cache-first`, …) serves the PREVIOUS
  // role's data.
  //
  // Measured in .demo: as a role whose permission set is empty, the server
  // correctly returned `pinnedDashboards: []`, but the left-nav still listed
  // 7 dashboards from the prior role — and a hard reload (which empties the
  // cache) immediately dropped it to 0. Nothing leaked that the user could
  // open (the backend gates every read), but the nav advertised dashboards
  // they have no access to.
  //
  // `resetStore()` is used rather than `clearStore()` precisely BECAUSE it
  // refetches active queries: clearing alone would leave mounted components
  // showing empty lists until something happened to re-query. It returns a
  // promise that rejects if a refetch fails; role switching must not break on
  // one bad query, so failures are swallowed with a warning.
  //
  // Declared ABOVE both call sites (the revocation effect and `setActiveRole`)
  // — a `const` arrow referenced from an effect defined earlier in the file is
  // a TDZ ReferenceError at first render, not a hoisted function.
  const resetRoleScopedCache = useCallback(() => {
    void apolloClient.resetStore().catch((error: unknown) => {
      console.warn('[ActiveRole] cache reset after role change failed', error);
    });
  }, [apolloClient]);

  // Initialize active role from localStorage or default to first role
  useEffect(() => {
    if (userLoading || initialized) return;

    const stored = localStorage.getItem(storageKey);

    if (stored && availableRoles.includes(stored)) {
      // Use stored role if it's still valid
      setActiveRoleState(stored);
      // Mirror the restored choice into the SERVER session too. Without this a
      // fresh page load leaves the session unset, so LiveView pages would run
      // as the full role set until the user manually re-picked a role.
      void persistActiveRole(stored);
    } else if (availableRoles.length > 0) {
      // Default to first role
      const defaultRole = availableRoles[0];
      setActiveRoleState(defaultRole);
      localStorage.setItem(storageKey, defaultRole);
      void persistActiveRole(defaultRole);
    }

    setInitialized(true);
  }, [userLoading, availableRoles, initialized]);

  // Update active role when available roles change (e.g., role revoked)
  useEffect(() => {
    if (!initialized || availableRoles.length === 0) return;

    // If current active role is no longer available, reset to first available
    if (activeRole && !availableRoles.includes(activeRole)) {
      const newRole = availableRoles[0];
      setActiveRoleState(newRole);
      localStorage.setItem(storageKey, newRole);
      // This path changes the effective role WITHOUT going through
      // `setActiveRole`, so it needs the same cache reset. A revoked role is
      // exactly when stale cached data is most dangerous: the results being
      // held were authored under access the user no longer has.
      //
      // Publish the header HERE rather than relying on the `[activeRole]`
      // effect below: that effect runs after this one, so resetting first would
      // fire refetches still carrying the revoked role. Setting it explicitly
      // makes the ordering local and obvious; the effect then re-sets the same
      // value, which is idempotent.
      setActiveRoleHeader(newRole);
      void persistActiveRole(newRole);
      resetRoleScopedCache();
    }
  }, [availableRoles, activeRole, initialized, storageKey, resetRoleScopedCache]);

  const setActiveRole = useCallback((role: string) => {
    setActiveRoleState(role);
    localStorage.setItem(storageKey, role);
    // Publish to the Apollo link BEFORE fetching so the featuresForRole query
    // (and every subsequent request) is evaluated as this role server-side.
    setActiveRoleHeader(role);
    // Then drop the outgoing role's cached results — ORDER MATTERS: the refetches
    // resetStore() triggers must carry the NEW header, so the header has to be
    // published first.
    resetRoleScopedCache();
    // Persist to the server session so LiveView surfaces (Dashboards, Admin
    // Console) narrow to this role too — they carry no headers.
    void persistActiveRole(role);
    // Fetch features for the new role
    fetchRoleFeatures({ variables: { role } });
  }, [fetchRoleFeatures, storageKey, resetRoleScopedCache]);

  // Keep the Apollo `X-Matrix-Active-Role` header in lockstep with the active
  // role, including the initial value restored from localStorage. Cleared on
  // unmount so a stale role can't ride a later session.
  useEffect(() => {
    setActiveRoleHeader(activeRole);
    return () => setActiveRoleHeader(null);
  }, [activeRole]);

  // Fetch features for the initial activeRole, unless the server already
  // preloaded them for the impersonated role — in which case seed
  // synchronously so `FeatureRouteGuard` doesn't defer rendering on the
  // initial mount. The lazy fetch still runs for non-impersonation mounts
  // and for manual role changes via `setActiveRole`.
  useEffect(() => {
    if (!initialized) return;

    // No assignable roles — nothing to fetch, so mark resolved to unblock
    // consumers. Falls through to the zero-grants path.
    if (!activeRole) {
      setRoleFeaturesResolved(true);
      return;
    }

    if (
      userInfo?.assumedRole === activeRole &&
      Array.isArray(userInfo.assumedRoleFeatures)
    ) {
      setRoleFeatures(userInfo.assumedRoleFeatures);
      setRoleFeaturesResolved(true);
      return;
    }

    fetchRoleFeatures({ variables: { role: activeRole } });
  }, [
    activeRole,
    initialized,
    fetchRoleFeatures,
    userInfo?.assumedRole,
    userInfo?.assumedRoleFeatures,
  ]);

  const value = useMemo<ActiveRoleContextType>(
    () => ({
      activeRole,
      availableRoles,
      isLoading: userLoading || !initialized,
      isLoadingFeatures,
      roleFeatures,
      roleFeaturesResolved,
      setActiveRole,
    }),
    [
      activeRole,
      availableRoles,
      userLoading,
      initialized,
      setActiveRole,
      roleFeatures,
      roleFeaturesResolved,
      isLoadingFeatures,
    ]
  );

  return <ActiveRoleContext value={value}>{children}</ActiveRoleContext>;
};

ActiveRoleProvider.displayName = 'ActiveRoleProvider';
